import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Tag, Modal } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import FilterSelect from 'components/Form/filterSelect';
import { getEmployeeUniformCardApi, changeSizeApi } from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge } from '../../constants';
import Breadcrumbs from 'components/Breadcrumbs';

const EmployeeUniformCard = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSizeChange, setShowSizeChange] = useState(false);
  const [selectedUniformForChange, setSelectedUniformForChange] = useState(null);
  const [newSize, setNewSize] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [changingSize, setChangingSize] = useState(false);

  const fetchCard = () => {
    setLoading(true);
    getEmployeeUniformCardApi(userId).then(response => {
      setCard(response.data?.data || null);
      setLoading(false);
    }).catch(() => { setLoading(false); });
  };

  useEffect(() => { fetchCard(); }, [userId]);

  const openSizeChange = (uniform) => {
    setSelectedUniformForChange(uniform);
    setNewSize(null);
    setChangeReason('');
    setShowSizeChange(true);
  };

  const handleSizeChange = () => {
    if (!newSize) {
      toast.error('Please select a new size', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setChangingSize(true);
    changeSizeApi({
      uniform_item: selectedUniformForChange.uniform_item_id,
      size: newSize.value,
      reason: changeReason,
      user_id: parseInt(userId)
    }).then(() => {
      toast.success('Size updated', { position: "top-right", autoClose: 3000, transition: Bounce });
      setChangingSize(false);
      setShowSizeChange(false);
      fetchCard();
    }).catch(e => {
      setChangingSize(false);
      toast.error(e.response?.data?.detail || 'Failed to change size', { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  if (loading || !card) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="text-center py-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
        </div>
      </div>
    );
  }

  const uniformColumns = [
    { title: 'Uniform', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'selected_size', render: (val) => val || <Tag color="orange">Not Selected</Tag> },
    { title: 'Last Issued', dataIndex: 'last_issued_date', render: (d) => d ? formatDateHandler(d) : '-' },
    { title: 'Next Replacement', dataIndex: 'next_replacement_date', render: (d) => d ? formatDateHandler(d) : '-' },
    {
      title: 'Status', dataIndex: 'current_status',
      render: (status) => <span style={getStatusBadge(status)}>{status}</span>
    },
    { title: 'Total Issued', dataIndex: 'total_issued' },
    {
      title: 'Action',
      render: (_, record) => (
        <Button label="Change Size" className="btn btn-sm btn-outline-primary"
          onClick={() => openSizeChange(record)} />
      )
    }
  ];

  const historyColumns = [
    { title: 'Uniform', dataIndex: 'uniform_item' },
    { title: 'Old Size', dataIndex: 'old_size', render: (val) => val || '-' },
    { title: 'New Size', dataIndex: 'new_size' },
    { title: 'Changed By', dataIndex: 'changed_by' },
    { title: 'Date', dataIndex: 'changed_at', render: (d) => formatDateHandler(d) },
    { title: 'Reason', dataIndex: 'reason', render: (val) => val || '-' }
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <Breadcrumbs maintitle="Employee Uniform Card" />
        <div className="d-flex mb-3">
          <Button label="Back" icon="fa fa-arrow-left" className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(-1)} />
        </div>
        {/* Employee Info */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-2"><strong>Name:</strong> <span className="ms-2">{card.employee_name}</span></div>
              <div className="col-md-3 mb-2"><strong>Emp No:</strong> <span className="ms-2">{card.emp_no}</span></div>
              <div className="col-md-3 mb-2"><strong>Position:</strong> <span className="ms-2">{card.position_name}</span></div>
              <div className="col-md-3 mb-2"><strong>Department:</strong> <span className="ms-2">{card.department_name}</span></div>
            </div>
          </div>
        </div>

        {/* Pending Selections */}
        {card.pending_selections?.length > 0 && (
          <div className="card mb-3 border-warning">
            <div className="card-header bg-warning text-dark"><h5 className="mb-0">Pending Size Selections</h5></div>
            <div className="card-body">
              {card.pending_selections.map(item => (
                <div key={item.uniform_item_id} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                  <span>{item.uniform_item_name}</span>
                  <Tag color="orange">Size not selected</Tag>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Uniforms Table */}
        <div className="card mb-3">
          <div className="card-header"><h5 className="mb-0">Assigned Uniforms</h5></div>
          <div className="card-body">
            <Table columns={uniformColumns} dataSource={card.uniforms || []}
              rowKey={(r) => r.uniform_item_id} pagination={false} scroll={{ x: "max-content" }} />
          </div>
        </div>

        {/* Size Change History */}
        {card.size_change_history?.length > 0 && (
          <div className="card">
            <div className="card-header"><h5 className="mb-0">Size Change History</h5></div>
            <div className="card-body">
              <Table columns={historyColumns} dataSource={card.size_change_history}
                rowKey={(r, i) => i} pagination={false} scroll={{ x: "max-content" }} />
            </div>
          </div>
        )}

        {/* Size Change Modal */}
        <Modal title="Change Size" open={showSizeChange} onCancel={() => setShowSizeChange(false)}
          footer={null} mask={false}>
          {selectedUniformForChange && (
            <div>
              <p><strong>Uniform:</strong> {selectedUniformForChange.uniform_item_name}</p>
              <p><strong>Current Size:</strong> {selectedUniformForChange.selected_size || 'Not selected'}</p>
              <div className="mb-3">
                <label className="col-form-label">New Size <span className="text-danger">*</span></label>
                <FilterSelect isClearable onChange={setNewSize} optionLabel="label" optionValue="value"
                  options={(selectedUniformForChange.available_sizes || []).map(s => ({
                    label: `${s.name} (${s.code})`, value: s.id
                  }))}
                  value={newSize} placeholder="Select New Size" isLoading={false} />
              </div>
              <div className="mb-3">
                <label className="col-form-label">Reason (optional)</label>
                <textarea className="form-control" rows={2} value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)} placeholder="Reason for change" />
              </div>
              <div className="submit-section">
                <Button label="Change Size" className="btn btn-primary submit-btn"
                  loading={changingSize} onClick={handleSizeChange} />
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default EmployeeUniformCard;
